import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Button, Empty } from "@/components/ui-kit";
import { ShieldCheck, Check, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function Droits() {
  const [caps, setCaps] = useState([]);
  const [roles, setRoles] = useState({});
  const [roleLabels, setRoleLabels] = useState({});
  const [editable, setEditable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/permissions").then((r) => {
      setCaps(r.data.capabilities || []);
      setRoles(r.data.roles || {});
      setRoleLabels(r.data.role_labels || {});
      setEditable(r.data.configurable_roles || []);
    }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const has = (role, key) => (roles[role] || []).includes(key);

  const toggle = (role, key) => {
    setRoles((prev) => {
      const cur = new Set(prev[role] || []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      return { ...prev, [role]: Array.from(cur) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { roles: {} };
      editable.forEach((r) => { payload.roles[r] = roles[r] || []; });
      const { data } = await api.put("/permissions", payload);
      setRoles(data.roles || roles);
      toast.success("Droits d'accès enregistrés");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Empty title="Chargement..." />;

  const groups = [...new Set(caps.map((c) => c.group))];

  return (
    <div data-testid="droits-page">
      <PageHeader title="Droits d'accès" subtitle="Configurez ce que chaque rôle peut voir et modifier. L'administrateur conserve toujours tous les droits."
        action={
          <div className="flex gap-2">
            <Button data-testid="reset-perms-btn" variant="outline" onClick={load}><RotateCcw size={16} /> Annuler</Button>
            <Button data-testid="save-perms-btn" onClick={save} disabled={saving}><Save size={16} /> {saving ? "..." : "Enregistrer"}</Button>
          </div>
        } />

      <div className="bg-card border border-border rounded-xl p-4 mb-6 flex items-center gap-3">
        <ShieldCheck className="text-primary shrink-0" size={22} />
        <p className="text-sm text-muted-foreground">
          <span className="text-foreground font-semibold">Administrateur</span> : accès total, non modifiable. Ajustez ci-dessous les rôles
          {" "}<span className="text-foreground font-semibold">{editable.map((r) => roleLabels[r] || r).join(", ")}</span>.
        </p>
      </div>

      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g} className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-secondary/40">
              <h3 className="font-display font-bold uppercase tracking-wide text-sm">{g}</h3>
            </div>
            <div className="divide-y divide-border">
              <div className="hidden sm:grid grid-cols-[1fr_repeat(4,72px)] gap-2 px-5 py-2 text-xs uppercase tracking-widest text-muted-foreground">
                <span>Capacité</span>
                <span className="text-center text-primary">Admin</span>
                {editable.map((r) => <span key={r} className="text-center">{roleLabels[r] || r}</span>)}
              </div>
              {caps.filter((c) => c.group === g).map((c) => (
                <div key={c.key} className="grid grid-cols-[1fr_repeat(4,72px)] gap-2 items-center px-5 py-3">
                  <span className="text-sm">{c.label}</span>
                  <div className="flex justify-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-primary/15 text-primary" title="Toujours autorisé">
                      <Check size={16} />
                    </span>
                  </div>
                  {editable.map((r) => (
                    <div key={r} className="flex justify-center">
                      <button
                        type="button"
                        data-testid={`toggle-${r}-${c.key}`}
                        onClick={() => toggle(r, c.key)}
                        role="switch"
                        aria-checked={has(r, c.key)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${has(r, c.key) ? "bg-primary" : "bg-secondary border border-border"}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${has(r, c.key) ? "translate-x-5" : ""}`} />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
